import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { useAuth } from '@/middlewares/auth/auth-context'
import { useMin } from '@/hooks/useBreakpoint'
import { useEventHub } from './EventHubContext'
import { formatLongDate, getEventHubBase, getSidebarRegState } from './eventHub.utils'

/** The sidebar's CTA block (EventHubSidebar sets the id). */
const CTA_BLOCK_ID = 'eh-reg-cta'
/** Root CSS var read by `body{padding-bottom}` so the footer is never covered by the bar. */
const HEIGHT_VAR = '--regbar-h'

/**
 * Mobile fixed registration bar (mock `#regBar`, William's request, no Figma reference).
 * Shown only below the `cols` breakpoint (≤1100, where the sidebar card is no longer sticky),
 * while the card's CTA block is scrolled out of the viewport, and never under the cookie
 * banner — so a single primary CTA is on screen at any time. Mounted by EventHub only on
 * public, non-leaderboard tabs.
 *
 * Left: status dot + word (open / sold out / "Open May 17 at 19:15") with the key date
 * under it; tapping it scrolls the card's CTA block back into view (no info is
 * duplicated). Right: one 44px button, by priority — athletes open → Register Now (filled);
 * athletes sold out → outlined "Join the waiting list" (mock `RB_CTA.full`, the registration
 * page lists the per-division waiting lists); spectator shop open → outlined "Spectator
 * tickets" (William, 15 Oct 2026; the left side then reads "● Spectators - Registration Open"
 * when athletes have no state of their own); otherwise → Join Staff when staff registration
 * is open.
 */
export default function EventHubRegBar() {
    const { t, i18n } = useTranslation(TranslationNS.EVENT_PAGE)
    const { t: tHome } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: tMonths } = useTranslation(TranslationNS.MONTHS)
    const language = i18n.resolvedLanguage
    const { competition, eventIdentifier } = useEventHub()
    const { cookiesConsentOpen } = useAuth()
    const { pathname } = useLocation()
    const isDesktop = useMin('cols')

    const base = getEventHubBase(pathname, eventIdentifier)
    const { regOpen, regSoon, regSoldOut, volOpen, specOpen, regColor, regLabel, regEnd, specColor, specEnd } =
        getSidebarRegState(competition, tHome, tMonths, language)
    const hasRegState = regOpen || regSoon || regSoldOut
    // tickets on sale once athlete registration is closed (the usual post-sold-out phase) still get a bar
    const hasState = hasRegState || specOpen

    const barRef = useRef(null)
    const [ctaOffscreen, setCtaOffscreen] = useState(false)

    // Mock updateRegBar(): on while the CTA block is rendered and fully out of the viewport.
    useEffect(() => {
        if (isDesktop) {
            setCtaOffscreen(false)
            return undefined
        }
        const update = () => {
            const r = document.getElementById(CTA_BLOCK_ID)?.getBoundingClientRect()
            setCtaOffscreen(Boolean(r) && r.height > 0 && (r.bottom < 0 || r.top > window.innerHeight))
        }
        update()
        // capture: the scroll may come from a nested scroll container
        document.addEventListener('scroll', update, { passive: true, capture: true })
        window.addEventListener('resize', update)
        return () => {
            document.removeEventListener('scroll', update, { capture: true })
            window.removeEventListener('resize', update)
        }
    }, [isDesktop, pathname])

    const visible = ctaOffscreen && hasState && !cookiesConsentOpen

    // Reserve the bar's height at the bottom of the page (the footer lives outside EventHub)
    // and flag the body so the Crisp launcher is lifted above the bar (style.scss).
    useEffect(() => {
        const root = document.documentElement
        if (visible) root.style.setProperty(HEIGHT_VAR, `${barRef.current?.offsetHeight || 0}px`)
        else root.style.removeProperty(HEIGHT_VAR)
        document.body.classList.toggle('eh-regbar-on', visible)
        return () => {
            root.style.removeProperty(HEIGHT_VAR)
            document.body.classList.remove('eh-regbar-on')
        }
    }, [visible])

    if (!visible) return null

    // Which audience drives the button: 'reg' | 'full' | 'spec' | 'staff' | null
    const mode = regOpen ? 'reg' : regSoldOut ? 'full' : specOpen ? 'spec' : volOpen ? 'staff' : null
    // Left side: the athlete status, or the spectator one when athletes have no state at all.
    const status = hasRegState
        ? { color: regColor, label: regLabel }
        : { color: specColor, label: `${t('Spectators')} - ${tHome('Registration Open')}` }
    // Open with no registration end date set → fall back to the event's start date.
    const eventStart = formatLongDate(competition?.date?.start?.iso, competition?.timezone, language)
    const closesOrStarts = (end) =>
        end ? t('Closes {{date}}', { date: end }) : eventStart && t('Starts {{date}}', { date: eventStart })
    const subLabel =
        mode === 'reg'
            ? closesOrStarts(regEnd)
            : mode === 'full'
            ? t('Waiting list available')
            : mode === 'spec'
            ? hasRegState
                ? t('Spectator tickets on sale')
                : closesOrStarts(specEnd)
            : mode === 'staff'
            ? t('Staff applications are open')
            : ''

    // Mock `.regbar .btn`: 44 / radius 5 / Comp 14 600, padding 0 20, no shadow.
    const btn =
        'inline-flex h-11 flex-none items-center justify-center rounded-[5px] px-5 font-meta text-sm font-semibold'
    const cta =
        mode === 'reg'
            ? {
                  label: t('Register Now'),
                  to: `${base}/registration`,
                  className: `${btn} bg-[#272727] text-white hover:text-white`,
              }
            : mode === 'full'
            ? { label: t('Join the waiting list'), to: `${base}/registration` }
            : mode === 'spec'
            ? { label: t('Spectator tickets'), to: `${base}/spectators/registration` }
            : mode === 'staff'
            ? { label: t('Join Staff'), to: `${base}/registration/volunteers` }
            : null

    const scrollToCard = () =>
        document.getElementById(CTA_BLOCK_ID)?.scrollIntoView({
            block: 'center',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        })

    return (
        <div
            ref={barRef}
            aria-label={t('Registration')}
            className="eventhub-regbar fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-[#dbdadf] bg-white px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom,0px))] shadow-[0_-10px_30px_-18px_rgba(14,16,23,0.35)] animate-[regbar-rise_.18s_ease] motion-reduce:animate-none"
        >
            <button
                type="button"
                onClick={scrollToCard}
                aria-label={t('Show registration details')}
                className="flex min-h-11 min-w-0 flex-1 cursor-pointer flex-col justify-center gap-0.5 border-0 bg-transparent p-0 text-left"
            >
                <span className="font-meta flex items-center gap-2 text-sm font-semibold leading-tight text-[#272727]">
                    {/* mock `.regbar-st .dot`: plain 7px solid dot in the status colour (no ripple) */}
                    <span
                        aria-hidden
                        className="size-[7px] flex-none rounded-full"
                        style={{ background: `var(${status.color})` }}
                    />
                    <span className="truncate">{status.label}</span>
                </span>
                {subLabel && (
                    <span className="font-meta truncate text-xs leading-tight text-[#5b5f6e]">{subLabel}</span>
                )}
            </button>

            {/* open → ink Register Now; sold out → outline waiting list; shop open → outline Spectator tickets;
                otherwise outline Join Staff when open */}
            {cta && (
                <Link
                    to={cta.to}
                    className={cta.className || `${btn} border border-[#272727] text-[#272727] hover:text-[#272727]`}
                >
                    {cta.label}
                </Link>
            )}
        </div>
    )
}
